/**
 * SummaryTables — MTD rollup cards rendered in a responsive grid:
 *   - NewVehicleSummary (by NEW Type)
 *   - UsedVehicleSummary (by retail USED Type — excludes wholesale + value center)
 *   - ValueCenterVehicleSummary (USED type ending in "VC" — own channel; hidden
 *     entirely when the month has no VC sales)
 *   - WholesaleVehicleSummary (USED type "W" only — dealer-to-dealer)
 *   - TotalsSummary (combined retail totals + Adjustments row — excludes both
 *     wholesale and value center)
 *
 * Wholesale and value center are split out server-side in summarizeMonth(); the
 * Used + Totals cards read summary.byType/summary.totals, which already exclude
 * type "W" and "*VC". Swaps (NEW type "S") keep their dollars in the retail
 * rollups but contribute 0 units server-side; their real count arrives as
 * TypeRollup.excludedUnits and renders in parentheses on the New card. All
 * values are MTD for the same month as the date picker.
 *
 * The "After Adjustments" rollup (the post-adjustment Actual figures) is the
 * key number, so it's emphasized in a green panel with larger bold text on the
 * New, Used, and Totals cards (shared AfterAdjustmentsPanel); the Wholesale card
 * keeps the plain styling.
 */

import React from 'react';
import { MtdSummary, TypeRollup } from '../../types/nutSheet';
import { formatCurrency, formatCurrencySigned, formatInt } from './formatters';

interface SummaryProps {
  summary: MtdSummary;
}

const headerCell = 'px-2 py-1 text-[11px] font-semibold text-gray-700 text-right whitespace-nowrap';
const headerLeft = 'px-2 py-1 text-[11px] font-semibold text-gray-700 text-left whitespace-nowrap';
const dataCell = 'px-2 py-1 text-xs text-right tabular-nums';
const dataLeft = 'px-2 py-1 text-xs text-left';

/**
 * The "After Adjustments" rollup (Units / Gross / Doc / [Holdback] / F&I /
 * Total / Avg) shown at the bottom of each summary card — the post-adjustment
 * (Actual) figures operators care about most. When `emphasize` is on (New /
 * Used / Totals) it's wrapped in a green panel with larger bold figures to draw
 * the eye; the Wholesale card passes `emphasize={false}` and keeps plain styling.
 */
const AfterAdjustmentsPanel: React.FC<{
  units: number;
  gross: number;
  doc: number;
  holdback: number;
  fAndI: number;
  total: number;
  avgPerUnit: number;
  showHoldback: boolean;
  emphasize: boolean;
}> = ({ units, gross, doc, holdback, fAndI, total, avgPerUnit, showHoldback, emphasize }) => {
  const colSpan = showHoldback ? 7 : 6;
  const colHead = emphasize
    ? 'px-2 py-1 text-[11px] font-semibold text-green-700 text-right whitespace-nowrap'
    : headerCell;
  const cell = emphasize
    ? 'px-2 py-1 text-sm font-bold text-green-900 text-right tabular-nums'
    : dataCell;
  return (
    <div
      className={
        emphasize
          ? 'mt-3 rounded-lg border-2 border-green-600 bg-green-100 p-2 overflow-x-auto'
          : 'overflow-x-auto mt-2'
      }
    >
      <table className="w-full">
        <thead>
          <tr className={emphasize ? '' : 'border-t border-gray-300'}>
            <th
              colSpan={colSpan}
              className={
                emphasize
                  ? 'px-2 py-1 text-xs font-bold text-green-800 text-left uppercase tracking-wide'
                  : 'px-2 py-1 text-[11px] font-semibold text-gray-700 text-left'
              }
            >
              After Adjustments
            </th>
          </tr>
          <tr className={emphasize ? 'border-b border-green-400' : 'border-b border-gray-400'}>
            <th className={colHead}>Units</th>
            <th className={colHead}>Gross</th>
            <th className={colHead}>Doc</th>
            {showHoldback && <th className={colHead}>Holdback</th>}
            <th className={colHead}>F&amp;I</th>
            <th className={colHead}>Total</th>
            <th className={colHead}>Avg / Unit</th>
          </tr>
        </thead>
        <tbody>
          <tr className={emphasize ? '' : 'font-semibold bg-gray-100'}>
            <td className={cell}>{formatInt(units)}</td>
            <td className={cell}>{formatCurrency(gross)}</td>
            <td className={cell}>{formatCurrency(doc)}</td>
            {showHoldback && <td className={cell}>{formatCurrency(holdback)}</td>}
            <td className={cell}>{formatCurrency(fAndI)}</td>
            <td className={cell}>{formatCurrency(total)}</td>
            <td className={cell}>{formatCurrency(avgPerUnit)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/**
 * Shared per-category card (New / Used / Wholesale): a by-Type breakdown whose
 * "Gross" column is PRE-adjustment (Σ Estimated Gross), an Adjustments line
 * Σ(Est − Actual), then an "After Adjustments" row where Gross = Est − adjustment
 * (= Actual) and Total = Gross + Doc + [Holdback] + F&I. `showHoldback` adds the
 * Holdback column (NEW only; ~0 for USED/WHOLESALE).
 */
const VehicleSummaryCard: React.FC<{
  title: string;
  emptyLabel: string;
  rows: TypeRollup[];
  showHoldback: boolean;
  /** Green-panel emphasis on the After Adjustments row (New / Used; off for Wholesale). */
  emphasizeAfterAdjustments: boolean;
}> = ({ title, emptyLabel, rows, showHoldback, emphasizeAfterAdjustments }) => {
  const subtotal = rows.reduce(
    (acc, r) => ({
      units: acc.units + r.units,
      gross: acc.gross + r.gross,
      doc: acc.doc + r.doc,
      holdback: acc.holdback + r.holdback,
      fAndI: acc.fAndI + r.fAndI,
      adjustment: acc.adjustment + r.adjustment,
    }),
    { units: 0, gross: 0, doc: 0, holdback: 0, fAndI: 0, adjustment: 0 },
  );
  // Post-adjustment gross = Est − Σ(Est − Actual) = Actual. Doc/Holdback/F&I are
  // not adjusted, so the after-adjustment Total folds them in unchanged.
  const grossAfter = subtotal.gross - subtotal.adjustment;
  const totalAfter = grossAfter + subtotal.doc + subtotal.holdback + subtotal.fAndI;
  const avgAfter = subtotal.units > 0 ? totalAfter / subtotal.units : 0;

  const breakdownCols = showHoldback ? 6 : 5; // Type + Units + Gross + Doc + [Holdback] + F&I
  const hasExcludedUnits = rows.some((r) => (r.excludedUnits ?? 0) > 0);

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-2">{title}</h3>
      <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-400">
            <th className={headerLeft}>Type</th>
            <th className={headerCell}>Units</th>
            <th className={headerCell}>Gross</th>
            <th className={headerCell}>Doc</th>
            {showHoldback && <th className={headerCell}>Holdback</th>}
            <th className={headerCell}>F&amp;I</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={breakdownCols} className="px-2 py-3 text-center text-xs italic text-gray-500">
                No {emptyLabel} transactions this month.
              </td>
            </tr>
          )}
          {rows.map((r) => (
            <tr key={r.type} className="border-b border-gray-300">
              <td className={dataLeft}>{r.type}</td>
              <td className={dataCell}>
                {r.excludedUnits ? `(${formatInt(r.excludedUnits)})` : formatInt(r.units)}
              </td>
              <td className={dataCell}>{formatCurrency(r.gross)}</td>
              <td className={dataCell}>{formatCurrency(r.doc)}</td>
              {showHoldback && <td className={dataCell}>{formatCurrency(r.holdback)}</td>}
              <td className={dataCell}>{formatCurrency(r.fAndI)}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="font-semibold bg-gray-100 border-t border-gray-400">
              <td className={dataLeft}>Total</td>
              <td className={dataCell}>{formatInt(subtotal.units)}</td>
              <td className={dataCell}>{formatCurrency(subtotal.gross)}</td>
              <td className={dataCell}>{formatCurrency(subtotal.doc)}</td>
              {showHoldback && <td className={dataCell}>{formatCurrency(subtotal.holdback)}</td>}
              <td className={dataCell}>{formatCurrency(subtotal.fAndI)}</td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {hasExcludedUnits && (
        <p className="mt-1 text-[11px] text-gray-500">
          Units in ( ) are Swaps — $ included, not counted in unit totals.
        </p>
      )}

      {rows.length > 0 && (
        <>
          <div className="mt-3 pt-2 border-t border-gray-300 text-xs flex justify-between">
            <span className="font-medium text-gray-700">Adjustments (Σ Actual − Est):</span>
            <span className="tabular-nums text-gray-900">{formatCurrencySigned(grossAfter - subtotal.gross)}</span>
          </div>

          <AfterAdjustmentsPanel
            units={subtotal.units}
            gross={grossAfter}
            doc={subtotal.doc}
            holdback={subtotal.holdback}
            fAndI={subtotal.fAndI}
            total={totalAfter}
            avgPerUnit={avgAfter}
            showHoldback={showHoldback}
            emphasize={emphasizeAfterAdjustments}
          />
        </>
      )}
    </div>
  );
};

export const NewVehicleSummary: React.FC<SummaryProps> = ({ summary }) => (
  <VehicleSummaryCard
    title="New Vehicle Summary (MTD)"
    emptyLabel="NEW"
    rows={summary.byType.filter((r) => r.category === 'NEW')}
    showHoldback
    emphasizeAfterAdjustments
  />
);

export const UsedVehicleSummary: React.FC<SummaryProps> = ({ summary }) => (
  <VehicleSummaryCard
    title="Used Vehicle Summary (MTD)"
    emptyLabel="USED"
    rows={summary.byType.filter((r) => r.category === 'USED')}
    showHoldback={false}
    emphasizeAfterAdjustments
  />
);

export const ValueCenterVehicleSummary: React.FC<SummaryProps> = ({ summary }) => {
  // Value Center (USED type ending in "VC") — its own rollup, excluded from the
  // retail Used card and the grand Totals (separated server-side). Not every
  // rooftop sells VC, so the card is hidden entirely when there are none.
  if (summary.valueCenter.byType.length === 0) return null;
  return (
    <VehicleSummaryCard
      title="Value Center Summary (MTD)"
      emptyLabel="VALUE CENTER"
      rows={summary.valueCenter.byType}
      showHoldback={false}
      emphasizeAfterAdjustments
    />
  );
};

export const WholesaleVehicleSummary: React.FC<SummaryProps> = ({ summary }) => (
  // Dealer-to-dealer wholesale (USED type "W") — its own rollup, excluded from
  // the retail Used card and the grand Totals (separated server-side).
  <VehicleSummaryCard
    title="Wholesale Vehicle Summary (MTD)"
    emptyLabel="WHOLESALE"
    rows={summary.wholesale.byType}
    showHoldback={false}
    emphasizeAfterAdjustments={false}
  />
);

export const TotalsSummary: React.FC<SummaryProps> = ({ summary }) => {
  const t = summary.totals;
  const total = t.gross + t.doc + t.holdback + t.fAndI;
  const avgPerUnit = t.units > 0 ? total / t.units : 0;

  const grossAfter = t.gross - summary.adjustments;
  const totalAfter = grossAfter + t.doc + t.holdback + t.fAndI;
  const avgAfter = t.units > 0 ? totalAfter / t.units : 0;

  return (
    <div className="bg-white rounded-lg shadow-md p-4">
      <h3 className="text-sm font-bold text-gray-900">Totals (MTD)</h3>
      <p className="text-[11px] text-gray-500 mb-2">
        Excludes Wholesale &amp; Value Center &middot; Swap $ included, Swap units not counted
      </p>
      <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-gray-400">
            <th className={headerCell}>Units</th>
            <th className={headerCell}>Gross</th>
            <th className={headerCell}>Doc</th>
            <th className={headerCell}>Holdback</th>
            <th className={headerCell}>F&amp;I</th>
            <th className={headerCell}>Total</th>
            <th className={headerCell}>Avg / Unit</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-gray-300">
            <td className={dataCell}>{formatInt(t.units)}</td>
            <td className={dataCell}>{formatCurrency(t.gross)}</td>
            <td className={dataCell}>{formatCurrency(t.doc)}</td>
            <td className={dataCell}>{formatCurrency(t.holdback)}</td>
            <td className={dataCell}>{formatCurrency(t.fAndI)}</td>
            <td className={dataCell}>{formatCurrency(total)}</td>
            <td className={dataCell}>{formatCurrency(avgPerUnit)}</td>
          </tr>
        </tbody>
      </table>
      </div>

      <div className="mt-3 pt-2 border-t border-gray-300 text-xs flex justify-between">
        <span className="font-medium text-gray-700">
          Adjustments (Σ Actual − Est):
        </span>
        <span className="tabular-nums text-gray-900">
          {formatCurrencySigned(grossAfter - t.gross)}
        </span>
      </div>

      <AfterAdjustmentsPanel
        units={t.units}
        gross={grossAfter}
        doc={t.doc}
        holdback={t.holdback}
        fAndI={t.fAndI}
        total={totalAfter}
        avgPerUnit={avgAfter}
        showHoldback
        emphasize
      />
    </div>
  );
};
